/**
 * ADMINISTRATIVO: AS REGRAS DA ESCRITA (10/10/2026, medição do item 16). Ofícios, termos de portabilidade e links públicos
 * de material. Funções puras que as telas usam e os testes cobram dos dois lados. Os confirms seguem a forma do
 * ConfirmDialog: título curto, nomes no corpo (a casca trunca o título numa linha).
 */
import { fmtDate } from './formatters';

// ---------------------------------------------------------------- Ofício

/**
 * D1: em Emitido o texto do documento não se edita. O servidor só valida (`validarAntesDeOficializar`) no emitir e no
 * enviar para assinatura, e o `update()` de um Emitido aceitava corpo novo: o PDF saía com o número oficial e um texto que
 * ninguém validou. Não há Emitido → Rascunho no backend; o caminho da casa é cancelar e criar outro (OF-2026/027 → 028).
 * Seguem editáveis o e-mail do destinatário, o destino e as observações: são o que se corrige antes de enviar.
 */
export const FRASE_DO_EMITIDO =
  'Emitido: o texto do documento não se edita, porque o servidor não revalida depois da emissão. Para corrigir, cancele e crie outro.';

export function textoTravado(oficio: { status?: string } | null | undefined): boolean {
  return oficio?.status === 'Emitido';
}

/** O que o Salvar manda. Em Emitido, só o que não é documento (o backend aceita PATCH parcial, campo a campo). */
export function payloadDaEdicao<T extends Record<string, any>>(oficio: { status?: string } | null | undefined, completo: T): Partial<T> {
  if (!textoTravado(oficio)) return completo;
  const { destinoTipo, destinatarioEmail, destinoPortalUrl, observacoes } = completo;
  return { destinoTipo, destinatarioEmail, destinoPortalUrl, observacoes } as unknown as Partial<T>;
}

/**
 * D3: Comunicado de Reajuste sem anualidade vinculada não oficializa. É a situação em que a anualidade não carregou (a
 * leitura é ADMIN) e o operador digitou os valores no corpo: a checagem do bloco contra o registro não roda.
 */
export function porqueNaoOficializar(oficio: { status?: string; tipo?: string; adjustmentId?: number | null }): string | null {
  if (oficio?.tipo !== 'Comunicado de Reajuste' || oficio.adjustmentId) return null;
  return 'Comunicado de Reajuste sem anualidade vinculada: o bloco de reajuste não seria conferido com o registro. '
    + (oficio.status === 'Rascunho' ? 'Vincule a anualidade em Editar.' : 'Cancele e crie outro com a anualidade.');
}

export function fraseDaAnualidadeQueNaoCarregou(motivo: string): string {
  return `Não consegui ler as anualidades desta assinatura (${motivo}). Isso não quer dizer que ela não tenha: sem a lista, o ofício não leva o bloco de reajuste, e o Comunicado de Reajuste não emite.`;
}

export function confirmacaoDeEmitirOficio(o: { numero: string; destinatarioNome?: string; assunto?: string }) {
  return {
    title: 'Emitir o ofício?',
    message: `${o.numero} para ${o.destinatarioNome || 'destinatário não informado'}: "${o.assunto || ''}".`,
    consequence: 'Vira documento oficial, com número e data de emissão. Depois disso o texto não se edita: para corrigir, cancela-se e cria-se outro.',
    confirmLabel: 'Emitir',
  };
}

export function confirmacaoDeCancelarOficio(o: { numero: string; assunto?: string; status?: string }): string {
  return [
    'Cancelar o ofício?',
    `${o.numero}: "${o.assunto || ''}". `
      + (o.status === 'Aguardando Assinatura' ? 'O envelope no ClickSign é cancelado. ' : '')
      + 'O número não se reaproveita: para corrigir, crie outro.',
  ].join('\n\n');
}

export function confirmacaoDeReabrirOficio(o: { numero: string }) {
  return {
    title: 'Reabrir o ofício?',
    message: `${o.numero} volta para "Emitido" e pode ser reenviado para assinatura.`,
    confirmLabel: 'Reabrir',
  };
}

/** D2: PDF de rascunho sai sem validação e sem marca no arquivo; o rótulo diz o que é. */
export function rotuloDoPdf(status?: string): { titulo: string; curto: string } {
  return status === 'Rascunho'
    ? { titulo: 'Baixar prévia (rascunho)', curto: 'Baixar prévia' }
    : { titulo: 'Baixar PDF', curto: 'Baixar' };
}

// ---------------------------------------------------------------- Termo de portabilidade

export function confirmacaoDeEmitirTermo(t: { numero: string; razaoSocial?: string; operadoraDoadora?: string }, quantos: number) {
  return {
    title: 'Emitir o termo?',
    message: `${t.numero} de ${t.razaoSocial || 'cliente não informado'}: ${quantos} número(s) a portar da ${t.operadoraDoadora || 'operadora não informada'}.`,
    consequence: 'Vira documento oficial. Depois disso não se edita; o servidor não confere o termo ao emitir, então confira números e CNPJ antes.',
    confirmLabel: 'Emitir',
  };
}

export function fraseDosDdrsQueNaoCarregaram(motivo: string): string {
  return `Não consegui ler os DDRs do cliente (${motivo}): a lista para escolher não apareceu. Digite os números à mão ou feche e abra de novo.`;
}

export function fraseDoClienteQueNaoCarregou(motivo: string): string {
  return `Não consegui ler o cadastro do cliente (${motivo}): razão social, CNPJ e endereço não foram preenchidos. Confira antes de salvar.`;
}

// ---------------------------------------------------------------- Link público de material

export type EstadoDoLink = { tipo: 'nenhum' } | { tipo: 'valido'; ate: string | null } | { tipo: 'expirado'; desde: string };

/**
 * D4: o servidor ROTACIONA o token expirado no mesmo clique que renova um válido (materials.service createPublicLink):
 * o link que já está com o cliente morre sem aviso. A tela distingue os dois.
 */
export function estadoDoLink(m: { publicToken?: string | null; publicTokenExpiresAt?: string | null }, agora: Date = new Date()): EstadoDoLink {
  if (!m?.publicToken) return { tipo: 'nenhum' };
  if (!m.publicTokenExpiresAt) return { tipo: 'valido', ate: null };
  return new Date(m.publicTokenExpiresAt).getTime() > agora.getTime()
    ? { tipo: 'valido', ate: m.publicTokenExpiresAt }
    : { tipo: 'expirado', desde: m.publicTokenExpiresAt };
}

export function rotuloDoLink(e: EstadoDoLink): { botao: string; selo: string | null } {
  if (e.tipo === 'nenhum') return { botao: 'Gerar link público (30 dias)', selo: null };
  if (e.tipo === 'expirado') return { botao: 'Gerar link NOVO (o anterior expirou)', selo: `Link EXPIRADO em ${fmtDate(e.desde)}` };
  return e.ate
    ? { botao: `Copiar link público (válido até ${fmtDate(e.ate)}; renova 30 dias)`, selo: `Link válido até ${fmtDate(e.ate)}` }
    : { botao: 'Copiar link público (sem prazo)', selo: 'Link sem prazo' };
}

export function confirmacaoDeLinkNovo(m: { title?: string }, desde: string) {
  return {
    title: 'Gerar um link NOVO?',
    message: `O link público de "${m.title || ''}" expirou em ${fmtDate(desde)}.`,
    consequence: 'O link enviado antes não volta a funcionar. Envie o novo ao cliente.',
    confirmLabel: 'Gerar link novo',
  };
}
